import React, { FC, useCallback, useMemo, useRef, useState } from 'react';

import { MessageType } from '@savgroup-front-common/types';

import {
  safeFormattedIntlString,
  SafeFormattedMessage,
} from '../../../formatters';
import { $Container, FieldMessage, Wrapper as FieldWrapper } from '../common';
import { getFinalFieldState } from '../common/helpers/getFinalFieldState';
import { FieldMessages } from '../common/helpers/getFinalFieldState.types';
import { Label } from '../common/Label/Label';

import {
  ParsedCountry,
  PhoneInput,
  PhoneInputProps,
  PhoneInputRefType,
} from './PhoneInput';

export interface PhoneNumberInputProps extends Omit<
  PhoneInputProps,
  'value' | 'onChange' | 'name' | 'placeholder' | 'disabled' | 'required'
> {
  value?: string;
  onChange?: (
    value: string,
    meta?: {
      country: ParsedCountry;
    },
  ) => void;
  name?: string;
  label?: MessageType | string;
  postLabel?: React.ReactNode;
  isRequired?: boolean;
  placeholder?: any;
  isDisabled?: boolean;
  errors?: FieldMessages;
  warnings?: FieldMessages;
  successes?: FieldMessages;
  isError?: boolean;
  isWarning?: boolean;
  isSuccess?: boolean;
  isHorizontal?: boolean;
  fullWidthLabel?: boolean;
  dataTestId?: string;
  defaultCountry?: string;
  disableBrowserAutocomplete?: boolean;
  isSmall?: boolean;
  shouldShowFieldMessage?: boolean;
  isFluid?: boolean;
}

const PhoneNumberInputBase: FC<
  PhoneNumberInputProps & { forwardedRef?: React.Ref<PhoneInputRefType> }
> = ({
  value = '',
  onChange = () => undefined,
  name,
  label = null,
  postLabel,
  isRequired = false,
  placeholder = '',
  isDisabled = false,
  errors = {},
  warnings = {},
  successes = {},
  isError = false,
  isWarning = false,
  isSuccess = false,
  isHorizontal = false,
  fullWidthLabel = false,
  dataTestId,
  forwardedRef,
  onFocus,
  onBlur,
  defaultCountry,
  disableBrowserAutocomplete,
  isSmall,
  shouldShowFieldMessage = true,
  isFluid = true,
  ...rest
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const [status, message] = useMemo(
    () =>
      getFinalFieldState({
        errors: { isStatus: isError, messages: errors },
        warnings: { isStatus: isWarning, messages: warnings },
        successes: { isStatus: isSuccess, messages: successes },
        name,
      }),
    [isError, isWarning, isSuccess, errors, warnings, successes, name],
  );

  const handleFormatPhoneNumber = useCallback((value: string) => {
    const cleaned = value
      .trim()
      .replace(/\s+/g, '')
      .replace('(0)', '')
      .replace(/^00([1-9]\d{0,2})/, '+$1')
      .replace(/\+3306/, '+336')
      .replace(/\+3307/, '+337')
      .replace(/\+3309/, '+339')
      .replace(/\+3301/, '+331');

    if (/^0[6791]\d{8}$/.test(cleaned)) {
      return '+33' + cleaned.slice(1);
    }

    if (/^\+0[6791]\d{8}$/.test(cleaned)) {
      return '+33' + cleaned.slice(2);
    }

    return cleaned;
  }, []);

  const handlePhoneChange = (
    value: string,
    meta?: {
      country: ParsedCountry;
    },
  ) => {
    const formattedValue = handleFormatPhoneNumber(value);

    onChange(formattedValue, meta);
  };

  const handlePasteCapture = (e: React.ClipboardEvent) => {
    e.preventDefault();

    const pastedValue = e.clipboardData.getData('Text');

    if (/^\+(\d{1,3})$/.test(value)) {
      return handlePhoneChange(`${value}${pastedValue}`);
    }

    if (/^\+(\d{1,3})/.test(pastedValue)) {
      return handlePhoneChange(pastedValue);
    } else if (/^\+(\d{1,3})/.test(value)) {
      return handlePhoneChange(
        `${value.replace(/^\+(\d{1,3})/, '+$1')}${pastedValue}`,
      );
    }

    return handlePhoneChange(pastedValue);
  };

  const ref = useRef<HTMLDivElement | null>(null);

  return (
    <$Container $isHorizontal={isHorizontal} ref={ref}>
      {label && (
        <Label
          htmlFor={name}
          isRequired={isRequired}
          status={status}
          postLabel={postLabel}
          fullWidth={fullWidthLabel}
          isHorizontal={isHorizontal}
        >
          {SafeFormattedMessage(label)}
        </Label>
      )}
      <FieldWrapper
        status={status}
        name={name}
        isHovered={isHovered}
        isFullWidth={isFluid}
      >
        <PhoneInput
          {...rest}
          isSmall={isSmall}
          data-testid={dataTestId}
          status={status}
          defaultCountry={defaultCountry?.toLowerCase()}
          ref={forwardedRef}
          value={value}
          onChange={handlePhoneChange}
          disabled={isDisabled}
          required={isRequired}
          placeholder={safeFormattedIntlString(placeholder)}
          onFocus={(e) => {
            if (onFocus) {
              onFocus(e as any);
            }
          }}
          onBlur={(e) => {
            if (onBlur) {
              onBlur(e as any);
            }
          }}
          inputStyle={{
            border: 'none',
            outline: 'none',
            width: '100%',
            background: 'transparent',
          }}
          inputProps={{
            ...(rest as any).inputProps,
            name,
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
            onPasteCapture: handlePasteCapture,
            'data-lpignore': 'true',
            autoComplete: 'off',
            'data-testid': dataTestId,
            disableBrowserAutocomplete: disableBrowserAutocomplete,
          }}
        />
      </FieldWrapper>
      {shouldShowFieldMessage && (
        <FieldMessage
          message={message}
          status={status}
          dataTestId={dataTestId}
        />
      )}
    </$Container>
  );
};

export const PhoneNumberInput = React.forwardRef<
  PhoneInputRefType,
  PhoneNumberInputProps
>((props, ref) => <PhoneNumberInputBase forwardedRef={ref} {...props} />);
